import {
  Menu,
  MenuButton,
  MenuItems,
  MenuLink,
  MenuPopover,
} from "@reach/menu-button"
import get from "lodash/get"
import React, { useContext } from "react"
import useMediaQuery from "../hooks/useMediaQuery"
import { urlHelper } from "../util"
import getLocalizedString from "../utils/getLocalizedString"
import tailwindConfig from "../utils/tailwindConfig"
import ThemeContext from "../context/ThemeContext"
import Link from "./LinkWrapper"
import { PageContext } from "./PageProvider"

const isCurrent = (item, entry) => {
  return (item.url || urlHelper(item)) === urlHelper(entry)
}

const isLevelLessThanEntryLevel = (item, entry) => {
  const itemLevel = ((item.url || urlHelper(item) || "").match(/\//g) || [])
    .length
  const entryLevel = ((urlHelper(entry) || "").match(/\//g) || []).length

  return itemLevel < entryLevel
}

type Link = {
  codename?: string
  id?: string
  label?: string
  url?: string
  type?: string
  order?: number
  hideFromNav?: boolean
  children?: Link[]
}

interface LocalNav {
  /**
   * An array of objects that contains the entries for the sub nav
   */
  links: Link[]
  /**
   * The content item for the current page
   */
  entry: unknown
}

const LocalNav = ({ links, entry }) => {
  const ctx = useContext(PageContext)
  const localizedStrings = ctx.localizedStrings
  const localizedOverview = getLocalizedString(
    localizedStrings,
    "localization_string___overview",
    "Overview"
  )

  const isLargeScreen = useMediaQuery(
    `(min-width: ${tailwindConfig.theme.screens.lg})`
  )
  const { hydrated: isClient } = React.useContext(ThemeContext)

  if (!links || !links.length) {
    return null
  }

  const [topPage, ...subpages] = links || []

  if (!topPage || (topPage && !Object.keys(topPage).length)) {
    return null
  }

  const hasSelectedChild = (parent, entry) => {
    if (parent.children && parent.children.length) {
      const foundChild = parent.children.find((link) => {
        return isCurrent(link, entry)
      })
      return !!foundChild
    }
  }

  return (
    <div
      className={`lg:overflow-x-auto shadow border-b border-gray-300 bg-gray-100 lg:scrollbar-width-0`}
    >
      <div className={`flex items-center lg:px-4`}>
        {/* Horizontal nav for large screens */}
        {isLargeScreen || !isClient ? (
          <>
            <Link
              to={topPage.url || urlHelper(topPage)}
              className="font-bold leading-tight py-2 px-4 lg:px-0 lg:border-r lg:border-gray-300 lg:pr-4 lg:mr-2"
            >
              {topPage.label ||
                get(topPage, "elements.meta__nav_title.value") ||
                get(topPage, "elements.title.value")}
            </Link>
            {subpages ? (
              <div className={`self-stretch hidden lg:block`}>
                <ul className={`flex items-center h-full mr-4`}>
                  {subpages.map((subpage) => {
                    const label =
                      subpage.label ||
                      get(subpage, "elements.meta__nav_title.value") ||
                      get(subpage, "elements.title.value")
                    const activeClassName = `border-red font-medium font-gray-900`
                    const inactiveClassName = `font-normal text-gray-700 border-transparent hover:border-gray-300 focus:border-gray-300`
                    const baseClassName = `h-full inline-flex items-center px-1 pt-3px border-b-3px text-sm`
                    const childActiveClassName = `bg-gray-200 font-medium`
                    const childInactiveClassName = `text-gray-700 hover:bg-gray-100 focus:bg-gray-100`
                    const childBaseClassName = `block py-2 px-2`

                    const className = (props) => {
                      return {
                        className: `${
                          hasSelectedChild(subpage, entry)
                            ? childActiveClassName
                            : (props.isPartiallyCurrent &&
                                isLevelLessThanEntryLevel(subpage, entry)) ||
                              props.isCurrent
                            ? activeClassName
                            : inactiveClassName
                        } ${baseClassName}`,
                      }
                    }
                    return (
                      <li
                        className={`pl-2 whitespace-nowrap h-full relative`}
                        key={subpage.id}
                      >
                        {subpage.children && subpage.children.length ? (
                          <Menu>
                            <MenuButton
                              className={`${
                                hasSelectedChild(subpage, entry) ||
                                isCurrent(subpage, entry)
                                  ? activeClassName
                                  : inactiveClassName
                              } ${baseClassName}`}
                            >
                              {label}{" "}
                              <i className="icon-chevron-down" aria-hidden></i>
                            </MenuButton>
                            <MenuPopover
                              className={`left-0 z-20 top-full absolute border border-gray-300 bg-white shadow-md rounded-b-lg overflow-hidden`}
                              style={{ minWidth: `140px` }}
                            >
                              <MenuItems
                                className={`divide-y text-sm`}
                                style={{ marginTop: 0 }}
                              >
                                {subpage.url &&
                                get(subpage, "type") !== "button" &&
                                get(subpage, "type") !== "redirect" ? (
                                  <MenuLink
                                    as={Link}
                                    to={subpage.url}
                                    getProps={(props) => ({
                                      className: props.isCurrent
                                        ? `${childActiveClassName} ${childBaseClassName}`
                                        : `${childInactiveClassName} ${childBaseClassName}`,
                                    })}
                                    className={`${childInactiveClassName} ${childBaseClassName}`}
                                    key={subpage.id}
                                  >
                                    {localizedOverview}
                                  </MenuLink>
                                ) : null}
                                {subpage.children.map((link, index) => {
                                  const childLabel =
                                    link.label ||
                                    get(
                                      link,
                                      "elements.meta__nav_title.value"
                                    ) ||
                                    get(link, "elements.title.value")
                                  const className = (props) => {
                                    return {
                                      className: `${
                                        props.isPartiallyCurrent
                                          ? childActiveClassName
                                          : childInactiveClassName
                                      } ${childBaseClassName}`,
                                    }
                                  }
                                  return (
                                    <MenuLink
                                      as={Link}
                                      to={link.url || urlHelper(link)}
                                      getProps={className}
                                      className={`${childInactiveClassName} ${childBaseClassName}`}
                                      key={link.id}
                                    >
                                      {childLabel}
                                    </MenuLink>
                                  )
                                })}
                              </MenuItems>
                            </MenuPopover>
                          </Menu>
                        ) : (
                          <Link
                            to={subpage.url || urlHelper(subpage)}
                            getProps={className}
                            className={`${inactiveClassName} ${baseClassName}`}
                          >
                            {label}
                          </Link>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
        {/* Popover nav for small screens */}
        {!isLargeScreen && isClient ? (
          <Menu>
            <MenuButton className="font-bold leading-tight py-2 px-4">
              {topPage.label ||
                get(topPage, "elements.meta__nav_title.value") ||
                get(topPage, "elements.title.value")}
              <i className="icon-chevron-down" aria-hidden></i>
            </MenuButton>
            <MenuPopover
              className={`z-40 w-full left-0 right-0 top-full absolute border border-gray-300 bg-white shadow-md rounded-b-lg overflow-hidden`}
            >
              <div>
                <MenuItems
                  className={`divide-y text-sm`}
                  style={{ marginTop: 0 }}
                >
                  <MenuLink
                    as={Link}
                    getProps={(props) => ({
                      className: `${
                        props.isCurrent
                          ? `bg-gray-200 font-medium`
                          : `text-gray-700 hover:bg-gray-100 focus:bg-gray-100`
                      } uppercase tracking-wide text-xs font-bold block py-2 px-4`,
                    })}
                    to={topPage.url || urlHelper(topPage)}
                  >
                    {topPage.label ||
                      get(topPage, "elements.meta__nav_title.value") ||
                      get(topPage, "elements.title.value")}
                    <i
                      className={`icon-chevron-right`}
                      style={{ width: `12px`, marginRight: `-12px` }}
                    />
                  </MenuLink>
                  {subpages.map((subpage) => {
                    const label =
                      subpage.label ||
                      get(subpage, "elements.meta__nav_title.value") ||
                      get(subpage, "elements.title.value")
                    const subpageActiveClassName = `bg-gray-200 font-medium`
                    const subpagePartiallyActiveClassName = `font-medium hover:bg-gray-100 focus:bg-gray-100`
                    const subpageInactiveClassName = `text-gray-700 hover:bg-gray-100 focus:bg-gray-100`
                    const subpageBaseClassName = `block py-2 pl-8 pr-4`
                    const className = (props) => {
                      return {
                        className: `${
                          hasSelectedChild(subpage, entry) // this check happens first to avoid an exact match for link highlighting a parent
                            ? subpagePartiallyActiveClassName
                            : props.isCurrent
                            ? subpageActiveClassName
                            : props.isPartiallyCurrent
                            ? subpagePartiallyActiveClassName
                            : subpageInactiveClassName
                        } ${subpageBaseClassName}`,
                      }
                    }
                    return (
                      <React.Fragment key={subpage.id}>
                        <MenuLink
                          as={Link}
                          to={subpage.url || urlHelper(subpage)}
                          getProps={className}
                          className={`${subpageInactiveClassName} ${subpageBaseClassName}`}
                        >
                          {label}
                        </MenuLink>
                        {subpage.children && subpage.children.length
                          ? (() => {
                              const childActiveClassName = `bg-gray-200 font-medium`
                              const childInactiveClassName = `text-gray-700 hover:bg-gray-100 focus:bg-gray-100`
                              const childBaseClassName = `block py-2 pl-12 pr-4`
                              const childClassName = (props) => {
                                return {
                                  className: `${
                                    props.isPartiallyCurrent
                                      ? childActiveClassName
                                      : childInactiveClassName
                                  } ${childBaseClassName}`,
                                }
                              }
                              return (
                                <>
                                  {subpage.children.map((child) => {
                                    return (
                                      <MenuLink
                                        as={Link}
                                        to={child.url || urlHelper(child)}
                                        getProps={childClassName}
                                        className={`${childInactiveClassName} ${childBaseClassName}`}
                                        key={child.id}
                                      >
                                        {child.label}
                                      </MenuLink>
                                    )
                                  })}
                                </>
                              )
                            })()
                          : null}
                      </React.Fragment>
                    )
                  })}
                </MenuItems>
              </div>
            </MenuPopover>
          </Menu>
        ) : null}
      </div>
    </div>
  )
}

export default LocalNav
